import { useDialogContext, useStoreState } from "@ariakit/react";
import { LetterheadSubmitError, Success, useForm } from "@indietabletop/appkit";
import type { SpaceGits } from "@indietabletop/types";
import { database } from "../../database.ts";
import { EditMode } from "../../engine/EditMode.ts";
import type { Vehicle } from "../../engine/Vehicle.ts";
import { FormDialog, FormDialogActions } from "../FormDialog/FormDialog.tsx";
import {
  useHandleSuccess,
  type SuccessReaction,
} from "../FormDialog/useHandleSuccess.ts";
import { FormDialogSubmitButton } from "../forms/DialogSubmitButton.tsx";
import { FormOptions, FormTextField } from "../forms/FormTextField.tsx";
import { useVehicleContext } from "../GangPage/ModelContext.tsx";
import { PreviewBanner } from "../GangPage/StashPreviewBanner.tsx";
import { useGangContext } from "../GangPage/useGang.ts";
import { VehicleFormOption } from "./VehicleFormOption.tsx";

type OnSubmit = (
  values: Pick<SpaceGits.VehicleData, "name" | "type">,
) => Promise<Success<SuccessReaction>>;

export function VehicleFormDialog(props: {
  vehicle?: Vehicle;
  overline?: string;
  heading: string;
  submit: string;
  onSubmit: OnSubmit;
}) {
  const { vehicle, overline, heading, submit, onSubmit: handleSubmit } = props;
  const { gang } = useGangContext();
  const dialog = useDialogContext();
  const handleSuccess = useHandleSuccess(dialog);
  const vehicleProfiles = gang.ruleset.vehicles.toArray();
  const priceMultiplier = gang.mode.isCampaignMode ? 2 : 1;

  const defaultValues: { name: string; typeId: string } = {
    name: vehicle?.data.name ?? "",
    typeId: vehicle?.data.type.id ?? vehicleProfiles.at(0)?.id ?? "",
  };

  const { form, submitName } = useForm({
    defaultValues: defaultValues,
    async onSubmit({ values }) {
      return handleSubmit({ name: values.name, type: { id: values.typeId } });
    },

    async onSuccess(values) {
      await handleSuccess(values);
    },
  });

  const values = useStoreState(form, (state) => state.values);

  return (
    <FormDialog heading={heading} overline={overline} form={form}>
      {!vehicle && (
        <PreviewBanner
          draftGang={
            gang.addVehicle({ type: { id: values.typeId }, name: "" }).gang
          }
          where={"on the streets of Gnork"}
        />
      )}

      <FormTextField
        name={form.names.name}
        placeholder="E.g. The Big Wroom"
        label="Name"
        autoFocusOnWide
      />

      {(!vehicle || vehicle.can(EditMode.CHANGE, "models")) && (
        <FormOptions label="Type">
          {vehicleProfiles.map((profile) => {
            return (
              <VehicleFormOption
                key={profile.id}
                profile={profile}
                name={form.names.typeId}
                priceMultiplier={priceMultiplier}
                type="radio"
              />
            );
          })}
        </FormOptions>
      )}

      <FormDialogActions>
        <LetterheadSubmitError name={submitName} />

        <FormDialogSubmitButton>{submit}</FormDialogSubmitButton>
      </FormDialogActions>
    </FormDialog>
  );
}

export function CreateVehicleDialog() {
  const { gang } = useGangContext();

  return (
    <VehicleFormDialog
      overline="City of Gnork"
      heading="Vehicle Registration Form"
      submit="Add"
      onSubmit={async (data) => {
        const vehicle = gang.addVehicle(data);
        const result = await database.gangs.updateOne(vehicle.gang.data);

        return result.mapSuccess((gangId) => ({
          navigateTo: `~/gangs/g/${gangId}/v/${vehicle.id}`,
        }));
      }}
    />
  );
}

export function EditVehicleDialog() {
  const vehicle = useVehicleContext();

  return (
    <VehicleFormDialog
      vehicle={vehicle}
      heading="Edit vehicle"
      submit="Save"
      onSubmit={async (data) => {
        const nextVehicle = vehicle.clone(data);
        const result = await database.gangs.updateOne(nextVehicle.gang.data);
        return result.mapSuccess(() => ({ message: "Saved" }));
      }}
    />
  );
}
